import React from 'react';
import { Button } from '@/components/ui/button';
import { ShoppingCart, ArrowRight, ChevronUp, ChevronDown } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { useSidebar } from '@/components/ui/sidebar';
import { useIsSidebarCompact } from '@/hooks/use-sidebar-compact';

interface FloatingTotalSummaryProps {
  itemCount: number;
  totalAmount: number;
  onProceed: () => void;
  proceedLabel?: string;
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
  showExpanded?: boolean;
  onToggleExpanded?: () => void;
  subtotal?: number;
  discountAmount?: number;
  taxAmount?: number;
  onCartOpen?: () => void;
}

const FloatingTotalSummary: React.FC<FloatingTotalSummaryProps> = ({
  itemCount,
  totalAmount,
  onProceed,
  proceedLabel = 'Continue',
  isLoading = false,
  disabled = false,
  className,
  showExpanded = false,
  onToggleExpanded,
  subtotal,
  discountAmount,
  taxAmount,
  onCartOpen,
}) => {
  const { state } = useSidebar();
  const isCompact = useIsSidebarCompact();
  const hasBreakdown = subtotal !== undefined || discountAmount !== undefined || taxAmount !== undefined;

  const sidebarLeft = isCompact ? '0' : state === 'expanded' ? 'var(--sidebar-width, 16rem)' : 'var(--sidebar-width-icon, 3rem)';

  const handleCartTap = () => {
    if (onCartOpen) {
      onCartOpen();
    } else if (onToggleExpanded) {
      onToggleExpanded();
    }
  };

  return (
    <div
      style={{ left: sidebarLeft }}
      className={cn(
        'fixed bottom-0 right-0 z-40',
        'bg-background border-t shadow-lg',
        'safe-area-bottom transition-[left] duration-200 ease-linear',
        className
      )}
    >
      {/* Expanded breakdown view */}
      {showExpanded && hasBreakdown && (
        <div className="px-4 py-3 border-b bg-muted/30 space-y-2">
          {subtotal !== undefined && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
          )}
          {discountAmount !== undefined && discountAmount > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Discount</span>
              <span className="text-green-600">-{formatCurrency(discountAmount)}</span>
            </div>
          )}
          {taxAmount !== undefined && taxAmount > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">VAT</span>
              <span>{formatCurrency(taxAmount)}</span>
            </div>
          )}
        </div>
      )}

      {/* Main summary bar */}
      <div className="px-4 py-3 flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={handleCartTap}
          className="flex items-center gap-3 flex-1 min-w-0"
        >
          <div className="relative flex-shrink-0">
            <ShoppingCart className="h-5 w-5 text-muted-foreground" />
            {itemCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                {itemCount > 99 ? '99+' : itemCount}
              </span>
            )}
          </div>
          
          <div className="flex flex-col min-w-0 text-left">
            <div className="flex items-center gap-1">
              <span className="text-lg font-bold truncate">
                {formatCurrency(totalAmount)}
              </span>
              {(hasBreakdown || onCartOpen) && (
                <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              )}
            </div>
            <span className="text-xs text-muted-foreground">
              {itemCount} {itemCount === 1 ? 'item' : 'items'} — tap to view
            </span>
          </div>
        </button>

        <Button
          onClick={onProceed}
          disabled={disabled || isLoading || itemCount === 0}
          size="lg"
          className="flex-shrink-0 gap-2 min-h-[44px]"
        >
          {proceedLabel}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default FloatingTotalSummary;
